// UI strings for Lukupino (lukupino/; engine pedagogy/reading.ts; Axel's decision of 2026-10-09):
// texts in which nearly every word is steady for you, read aloud — the page, the door on Tänään
// after the day's cards, the week hub's line and the lesson's door. Style: docs/FINNISH-STYLE.md
// (sinä-muoto, no jargon, no intro text on the page). Labels say where a text comes from and never
// more: Peterson's, a lesson's practice sentence, ours, AI-checked (never "tarkistettu" alone), the
// partner's corrected letter, community.
import { defineArea } from '../area';

const texts = (n: number) => (n === 1 ? '1 teksti odottaa' : `${n} tekstiä odottaa`);

export const lukupinoFi = {
  title: 'Lukupino',
  /** Tänään's door: its second line. */
  doorDetail: (n: number) => `${texts(n)} · noin 5 min`,
  /** The week hub's line: "Lue · 3 tekstiä odottaa". */
  hubRead: 'Lue',
  waiting: texts,
  /** Before the lesson that opens it. */
  closed: (n: number) => `Lukupino avautuu oppitunnin ${n} kanssa.`,
  loading: 'Etsitään tekstejä…',
  empty: 'Pinossa ei ole nyt tekstejä. Uusia tulee, kun opit korteista lisää sanoja.',
  /** The empty state's one action. */
  toCards: 'Päivän kortit',
  aloud: 'Lue teksti ääneen.',
  progress: (i: number, n: number) => `Teksti ${i}/${n}`,
  /** Above a text with a word or two you do not know yet: those words first. */
  glossH: 'Uudet sanat ensin',
  showTranslation: 'Näytä käännös',
  record: 'Äänitä lukemisesi',
  read: 'Luettu',
  done: 'Päivän tekstit on luettu.',
  more: 'Lue lisää tekstejä',
  back: 'Takaisin Tänään-sivulle',
  /** A text read before, back again: its second or third reading. */
  round: (n: number): string => (n <= 2 ? 'toinen lukukerta' : 'kolmas lukukerta'),
  /** The sitting's group (narrow reading: one character, one lesson or one topic). */
  group: {
    lesson: (n: number) => `Oppitunti ${n}`,
    theme: (topic: string) => `Aihe: ${topic}`,
    stack: 'Arjen lauseet',
    letters: 'Kumppanisi kirjeet',
    community: 'Yhteisön esimerkit',
    peterson: 'Petersonin esimerkit',
    show: 'Sarjan kohtaukset',
  },
  /** Where a text comes from (small print under it). */
  label: {
    example: 'Petersonin esimerkki',
    scene: 'sarjan kohtaus',
    part: (n: number) => `osa ${n}`,
    practice: 'harjoituslause · ei Petersonin',
    ours: 'meidän käännös',
    letter: 'kumppanisi kirje korjauksineen · ei Petersonin',
  },
  /** The lesson's door (<Lukupino /> in lesson 4). */
  lessonOpen: 'Avaa Lukupino',
  lessonNone: 'Pinossa ei ole vielä tekstejä. Niitä tulee, kun osaat korteista enemmän tämän ja aiempien oppituntien sanoja.',
};

const textsEn = (n: number) => (n === 1 ? '1 text waiting' : `${n} texts waiting`);

export const lukupinoEn: typeof lukupinoFi = {
  title: 'Reading pile',
  doorDetail: (n: number) => `${textsEn(n)} · about 5 min`,
  hubRead: 'Read',
  waiting: textsEn,
  closed: (n: number) => `The reading pile opens with lesson ${n}.`,
  loading: 'Looking for texts…',
  empty: 'No texts in the pile now. New ones come as you learn more words from your cards.',
  toCards: 'Today’s cards',
  aloud: 'Read the text aloud.',
  progress: (i: number, n: number) => `Text ${i}/${n}`,
  glossH: 'New words first',
  showTranslation: 'Show the translation',
  record: 'Record your reading',
  read: 'Read',
  done: 'Today’s texts are read.',
  more: 'Read more texts',
  back: 'Back to Today',
  round: (n: number) => (n <= 2 ? 'second reading' : 'third reading'),
  group: {
    lesson: (n: number) => `Lesson ${n}`,
    theme: (topic: string) => `Topic: ${topic}`,
    stack: 'Everyday sentences',
    letters: 'Your partner’s letters',
    community: 'Community examples',
    peterson: 'Peterson’s examples',
    show: 'Show scenes',
  },
  label: {
    example: 'Peterson’s example',
    scene: 'show scene',
    part: (n: number) => `part ${n}`,
    practice: 'practice sentence · not Peterson’s',
    ours: 'our translation',
    letter: 'your partner’s letter with its corrections · not Peterson’s',
  },
  lessonOpen: 'Open the reading pile',
  lessonNone: 'No texts in the pile yet. They come as you know more of this and the earlier lessons’ words from your cards.',
};

export const area = defineArea('lukupino', lukupinoFi, lukupinoEn);

declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof lukupinoFi> {}
}
